import { useEffect, useMemo } from 'react'
import { Popover, Text } from '@mantine/core'
import { RichTextEditor, useRichTextEditorContext } from '@mantine/tiptap'
import { Extension, Mark } from '@tiptap/core'
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import CharacterCount from '@tiptap/extension-character-count'
import emojiData from '@emoji-mart/data'
import EmojiPicker from '@emoji-mart/react'
import {
  IconBold, IconItalic, IconMoodSmile, IconPlus,
  IconStrikethrough, IconUnderline,
} from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { fieldLabel } from '../../utils/uiLayoutV2'
import classes from './V2Form.module.css'

const ALIGNMENTS = ['left', 'center', 'right']
const SLOGAN_MAX_CHARACTERS = 35

// The old editor's slogan counter colours.
const counterColor = (count) => {
  if (count <= 5 || count >= 30) return 'red'
  if (count <= 14 || count >= 25) return 'orange'
  return 'green'
}

// The slogan's accent ([[…]]) and highlight ({{…}}) are template tokens in the stored text;
// in the editor they are marks, so they can be seen and toggled like bold.
const AccentMark = Mark.create({
  name: 'accent',
  inclusive: false,
  parseHTML: () => [{ tag: 'span[data-accent]' }],
  renderHTML: () => ['span', { 'data-accent': 'true' }, 0],
})

const HighlightMark = Mark.create({
  name: 'sloganHighlight',
  inclusive: false,
  parseHTML: () => [{ tag: 'span[data-highlight]' }],
  renderHTML: () => ['span', { 'data-highlight': 'true' }, 0],
})

// Paragraph / list-item alignment stored as data-align (the 3.2.0 sanitizer's attribute);
// @tiptap/extension-text-align would write inline styles the ad strips.
const BlockAlign = Extension.create({
  name: 'blockAlign',
  addGlobalAttributes: () => [{
    types: ['paragraph', 'listItem'],
    attributes: {
      align: {
        default: null,
        parseHTML: (element) => (ALIGNMENTS.includes(element.getAttribute('data-align')) ? element.getAttribute('data-align') : null),
        renderHTML: (attributes) => (attributes.align ? { 'data-align': attributes.align } : {}),
      },
    },
  }],
})

// A button label is one line: Enter and Shift+Enter do nothing.
const SingleLine = Extension.create({
  name: 'singleLine',
  addKeyboardShortcuts: () => ({ Enter: () => true, 'Shift-Enter': () => true, 'Mod-Enter': () => true }),
})

/** Stored value → editor HTML: tokens become mark spans. */
export function toEditorHtml(value) {
  if (typeof value !== 'string' || !value) return ''
  return value
    .replace(/\[\[([\s\S]*?)\]\]/g, '<span data-accent="true">$1</span>')
    .replace(/\{\{([\s\S]*?)\}\}/g, '<span data-highlight="true">$1</span>')
}

/**
 * Editor HTML → stored value: marks back to tokens, list items without the paragraph
 * tiptap wraps their text in, and a lone plain paragraph stored inline (as 3.1.1 stored
 * every slogan). `inline` keeps only the first paragraph's content (button labels).
 */
export function fromEditorHtml(html, { inline = false } = {}) {
  if (typeof document === 'undefined') return html
  const doc = document.implementation.createHTMLDocument('')
  const root = doc.createElement('div')
  root.innerHTML = html || ''

  root.querySelectorAll('span[data-accent], span[data-highlight]').forEach((span) => {
    const accent = span.hasAttribute('data-accent')
    span.replaceWith(accent ? '[[' : '{{', ...Array.from(span.childNodes), accent ? ']]' : '}}')
  })

  root.querySelectorAll('li').forEach((item) => {
    const paragraphs = Array.from(item.children).filter((child) => child.tagName === 'P')
    paragraphs.forEach((paragraph, index) => {
      const align = paragraph.getAttribute('data-align')
      if (align && !item.hasAttribute('data-align')) item.setAttribute('data-align', align)
      if (index > 0) paragraph.before(doc.createElement('br'))
      paragraph.replaceWith(...Array.from(paragraph.childNodes))
    })
  })

  const blocks = Array.from(root.childNodes).filter((node) => node.nodeType !== 3 || node.textContent.trim())
  const empty = !root.textContent.replace(/​/g, '').trim()
    && !root.querySelector('li, br, img')
  if (empty) return ''

  if (inline) {
    const first = root.querySelector('p')
    return (first ? first.innerHTML : root.innerHTML).trim()
  }
  if (blocks.length === 1 && blocks[0].tagName === 'P' && !blocks[0].attributes.length) {
    return blocks[0].innerHTML.trim()
  }
  return root.innerHTML.trim()
}

function EmojiControl() {
  const { t } = useTranslation()
  const { editor } = useRichTextEditorContext()
  return (
    <Popover width={352} position="bottom-start" shadow="md" withinPortal>
      <Popover.Target>
        <RichTextEditor.Control aria-label={t('editorV2.form.common.insertEmoji')} title={t('editorV2.form.common.insertEmoji')} className={classes.rteControl}>
          <IconMoodSmile size={18} stroke={1.6} />
        </RichTextEditor.Control>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <EmojiPicker data={emojiData} onEmojiSelect={({ native }) => editor?.chain().focus().insertContent(native).run()} />
      </Popover.Dropdown>
    </Popover>
  )
}

function Control({ label, active, disabled, onClick, children }) {
  return (
    <RichTextEditor.Control
      aria-label={label}
      title={label}
      active={active}
      disabled={disabled}
      onClick={onClick}
      className={classes.rteControl}
    >
      {children}
    </RichTextEditor.Control>
  )
}

/**
 * Figma "rich editor" (Slogan, Button/CTA): white 48px toolbar of 28px bordered controls over
 * a #F8F9FA writing area. `rich-block` (slogan) has the old editor's grey toolbar with
 * "+ Add Highlight" and the `current / 35` counter (AD-243); lists and alignment of older slogans
 * are kept but can no longer be added. `rich-inline` (button label) is a single line with
 * B / I / U / S and emoji.
 */
export function RichTextV2({ field, value, onChange, error, disabled = false, sectionUi = {} }) {
  const { t } = useTranslation()
  const block = field.validation?.allowedMarkup === 'rich-block'
  const placeholder = field.ui?.placeholder || ''

  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: false,
      blockquote: false,
      codeBlock: false,
      code: false,
      horizontalRule: false,
      link: false,
      bulletList: block ? {} : false,
      orderedList: block ? {} : false,
      listItem: block ? {} : false,
      listKeymap: block ? {} : false,
      hardBreak: block ? {} : false,
    }),
    Placeholder.configure({ placeholder }),
    BlockAlign,
    ...(block ? [AccentMark, HighlightMark, CharacterCount] : [SingleLine]),
  ], [block, placeholder])

  const editor = useEditor({
    extensions,
    content: toEditorHtml(value),
    editable: !disabled,
    // "- " or "1. " would still start a list in a slogan.
    enableInputRules: !block,
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: current }) => {
      onChange(fromEditorHtml(current.getHTML(), { inline: !block }))
    },
  }, [extensions])

  useEffect(() => {
    if (!editor) return
    const current = fromEditorHtml(editor.getHTML(), { inline: !block })
    if (current !== (value || '')) editor.commands.setContent(toEditorHtml(value), { emitUpdate: false })
  }, [block, editor, value])

  useEffect(() => { editor?.setEditable(!disabled) }, [disabled, editor])

  const label = fieldLabel(field)
  const highlightActive = Boolean(editor?.isActive('sloganHighlight'))
  const characters = block ? editor?.storage.characterCount.characters() || 0 : 0

  return (
    <div className={classes.stack}>
      <span className={classes.label}>{label}</span>
      <RichTextEditor editor={editor} classNames={{ root: block ? `${classes.rte} ${classes.rteBlock}` : classes.rte, toolbar: classes.rteToolbar, content: classes.rteContent, controlsGroup: classes.rteGroup }}>
        <RichTextEditor.Toolbar sticky={false}>
          <RichTextEditor.ControlsGroup>
            <Control label={t('editorV2.form.slogan.bold')} active={editor?.isActive('bold')} onClick={() => editor?.chain().focus().toggleBold().run()}>
              <IconBold size={18} stroke={1.6} />
            </Control>
            <Control label={t('editorV2.form.slogan.italic')} active={editor?.isActive('italic')} onClick={() => editor?.chain().focus().toggleItalic().run()}>
              <IconItalic size={18} stroke={1.6} />
            </Control>
            <Control label={t('editorV2.form.slogan.underline')} active={editor?.isActive('underline')} onClick={() => editor?.chain().focus().toggleUnderline().run()}>
              <IconUnderline size={18} stroke={1.6} />
            </Control>
            <Control label={t('editorV2.form.slogan.strikethrough')} active={editor?.isActive('strike')} onClick={() => editor?.chain().focus().toggleStrike().run()}>
              <IconStrikethrough size={18} stroke={1.6} />
            </Control>
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <EmojiControl />
          </RichTextEditor.ControlsGroup>
          {block && (
            <>
              {field.ui?.highlight && (
                <button
                  type="button"
                  className={classes.smallButton}
                  data-active={highlightActive ? 'true' : undefined}
                  disabled={disabled}
                  title={t('editorV2.form.slogan.highlightHint')}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    if (!editor) return
                    if (editor.state.selection.empty && !highlightActive) {
                      editor.chain().focus().run()
                      return
                    }
                    editor.chain().focus().toggleMark('sloganHighlight').run()
                  }}
                >
                  <IconPlus size={14} stroke={1.8} />
                  {highlightActive ? t('editorV2.form.slogan.removeHighlight') : (sectionUi.highlightLabel || t('editorV2.form.slogan.addHighlight'))}
                </button>
              )}
              <Text size="sm" fw={700} c={counterColor(characters)} ml="auto">
                {characters} / {SLOGAN_MAX_CHARACTERS}
              </Text>
            </>
          )}
        </RichTextEditor.Toolbar>
        <RichTextEditor.Content data-single={block ? undefined : 'true'} />
      </RichTextEditor>
      {error && <p className={classes.error}>{error}</p>}
    </div>
  )
}
